/** Hình thức đóng học phí: Trả góp ngân hàng */
export const BANK_INSTALLMENT_TUITION_PAYMENT_ID = 2;

/** OnePay payment method id (method_payment_id) */
export const ONEPAY_METHOD_ID = 4;

/** Mục đích / hình thức trả góp OnePay */
export const ONEPAY_INSTALLMENT = {
  FULL: 0, // Trả thẳng
  MONTH_6: 6,
  MONTH_9: 9,
  MONTH_12: 12,
};

export const ONEPAY_INSTALLMENT_OPTIONS = [
  { value: ONEPAY_INSTALLMENT.FULL, label: 'Trả thẳng' },
  { value: ONEPAY_INSTALLMENT.MONTH_6, label: 'Trả góp trong 6 tháng' },
  { value: ONEPAY_INSTALLMENT.MONTH_9, label: 'Trả góp trong 9 tháng' },
  { value: ONEPAY_INSTALLMENT.MONTH_12, label: 'Trả góp trong 12 tháng' },
];

/**
 * Phương án đóng phí trả góp (lấy từ voucher/promotion trên hợp đồng)
 * 1 = Khách hàng tự đóng phí
 * 2 = Trung tâm hỗ trợ đóng phí
 */
export const ONEPAY_FEE_PLAN = {
  CUSTOMER: 1,
  CENTER: 2,
};

export const ONEPAY_FEE_PLAN_LABEL = {
  [ONEPAY_FEE_PLAN.CUSTOMER]: 'Khách hàng tự đóng phí',
  [ONEPAY_FEE_PLAN.CENTER]: 'Trung tâm hỗ trợ đóng phí',
};

const INSTALLMENT_MONTHS = [
  ONEPAY_INSTALLMENT.MONTH_6,
  ONEPAY_INSTALLMENT.MONTH_9,
  ONEPAY_INSTALLMENT.MONTH_12,
];

export function toInstallmentFee(value) {
  if (value === null || value === undefined || value === '') return null;
  const fee = Number(value);
  return Number.isNaN(fee) ? null : fee;
}

function normalizeInstallmentFeePayer(raw) {
  if (raw == null || raw === '') return null;
  const num = Number(raw);
  if (num === ONEPAY_FEE_PLAN.CUSTOMER || num === ONEPAY_FEE_PLAN.CENTER) {
    return num;
  }
  return null;
}

export function getSelectedInstallmentPromotion(vouchers = []) {
  if (!Array.isArray(vouchers) || !vouchers.length) return null;

  for (const voucher of vouchers) {
    if (!voucher || typeof voucher !== 'object') continue;
    const payer = normalizeInstallmentFeePayer(
      voucher.installmentFeePayer ?? voucher.installment_fee_payer
    );
    if (payer != null && payer >= 1) return voucher;
  }
  return null;
}

/**
 * Lấy installment_fee_payer từ voucher đã add vào hợp đồng
 * (chỉ khi hình thức đóng học phí = Trả góp ngân hàng).
 *
 * @param {Array} vouchers
 * @param {number|string|null} tuitionPaymentId
 * @returns {number|null}
 */
export function getInstallmentFeePlanFromVouchers(vouchers = [], tuitionPaymentId = null) {
  if (Number(tuitionPaymentId) !== BANK_INSTALLMENT_TUITION_PAYMENT_ID) {
    return null;
  }
  const promo = getSelectedInstallmentPromotion(vouchers);
  if (!promo) return null;
  return normalizeInstallmentFeePayer(
    promo.installmentFeePayer ?? promo.installment_fee_payer
  );
}

/**
 * % phí trả góp lưu vào installments_number: 2 (trả thẳng) | % từ voucher (trả góp) | 0
 */
export function getOnePayFeePercent(installment, vouchersOrFeePlan, tuitionPaymentId) {
  const purpose = Number(installment);
  if (purpose === ONEPAY_INSTALLMENT.FULL) {
    return 2;
  }
  if (!INSTALLMENT_MONTHS.includes(purpose)) {
    return 0;
  }

  // Nếu truyền mảng vouchers
  if (Array.isArray(vouchersOrFeePlan)) {
    const promo = getSelectedInstallmentPromotion(vouchersOrFeePlan);
    if (!promo) return 0;
    const fee = toInstallmentFee(promo.installmentFee ?? promo.installment_fee);
    return fee != null ? fee : 0;
  }

  // Fallback nếu truyền feePlan dạng số
  if (typeof vouchersOrFeePlan === 'number') {
    if (vouchersOrFeePlan === ONEPAY_FEE_PLAN.CUSTOMER) return 9;
    if (vouchersOrFeePlan === ONEPAY_FEE_PLAN.CENTER) return 2.5;
  }

  return 0;
}

/**
 * Tính phí trả góp OnePay: amount * (feePercent / 100)
 *
 * @param {number} amount Số tiền khách hàng thanh toán
 * @param {number} feePercent % phí trả góp (hoặc installment nếu gọi kiểu cũ)
 * @param {number|null} feePlan
 * @returns {number}
 */
export function calcOnePayInstallmentFee(amount, feePercent, feePlan) {
  const payAmount = Number(amount) || 0;
  if (payAmount <= 0) return 0;

  // Nếu gọi theo signature cũ (amount, installment, feePlan)
  if (feePlan !== undefined && [ONEPAY_INSTALLMENT.FULL, ...INSTALLMENT_MONTHS].includes(Number(feePercent))) {
    const percent = getOnePayFeePercent(feePercent, feePlan);
    return Math.round(payAmount * (percent / 100));
  }

  const percent = Number(feePercent) || 0;
  if (percent <= 0) return 0;
  return Math.round(payAmount * (percent / 100));
}

/**
 * Số tiền khách hàng cần thanh toán thẻ = số tiền thanh toán + phí trả góp
 */
export function calcOnePayCardAmount(amount, installmentFee) {
  return (Number(amount) || 0) + (Number(installmentFee) || 0);
}

export function getOnePayNotice(installment, feePercent) {
  const purpose = Number(installment);
  const percent = Number(feePercent) || 0;
  if (INSTALLMENT_MONTHS.includes(purpose)) {
    if (percent > 0) {
      return `Khách hàng thanh toán trả góp cần trả thêm phí ${percent}%`;
    }
    return 'Khách hàng thanh toán trả góp: phí 0%';
  }
  return 'Khách hàng thanh toán trả thẳng cần trả thêm phí 2%';
}

export function getVisibleInstallmentOptions(tuitionPaymentId) {
  if (Number(tuitionPaymentId) === BANK_INSTALLMENT_TUITION_PAYMENT_ID) {
    return ONEPAY_INSTALLMENT_OPTIONS;
  }
  return ONEPAY_INSTALLMENT_OPTIONS.filter(opt => opt.value === ONEPAY_INSTALLMENT.FULL);
}

export function buildPaymentOptionLabel(methodPaymentIds, methodOptions, onepayInstallment) {
  const ids = Array.isArray(methodPaymentIds) ? methodPaymentIds.map(Number) : [];
  if (!ids.length) return '';

  const cardPlanLabel = ONEPAY_INSTALLMENT_OPTIONS.find(
    opt => opt.value === Number(onepayInstallment)
  )?.label;

  return methodOptions
    .filter(opt => ids.includes(Number(opt.id)))
    .map(opt => {
      if (Number(opt.id) === ONEPAY_METHOD_ID && cardPlanLabel) {
        return `${opt.name} - ${cardPlanLabel}`;
      }
      return opt.name;
    })
    .join(', ');
}
